// ImprestSurrenderForm.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Chip,
  Paper,
  LinearProgress,
  Stepper,
  Step,
  StepButton,
  StepLabel,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  ReceiptLong,
  CalendarToday,
  AttachMoney,
  Description,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";

import { generateAndExecuteMutation, graphqlQuery } from "../../../data/Axios/DynamicService";
import { HRService } from "../../../config";
import { approvedDisbursedImprests, approvedImprestApplicationsNotSurrendered, imprestApplicationsByUser } from "../../../data/Queries/HRmanagement/leavemanagement";
import { tokens } from "../../../theme";

const base_url = HRService.uri;

// ---------------------------------------------------------------------
// ImprestSurrenderForm – ONE-PAGE, NO DynamicForm
// ---------------------------------------------------------------------
const ImprestSurrenderForm = ({ data, isEditing, onClose, onAction }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id, 10);
  const companyId = parseInt(decodedToken.CompanyId, 10);

  // -----------------------------------------------------------------
  // State
  // -----------------------------------------------------------------
  const [imprestApplications, setImprestApplications] = useState([]);
  const [selectedImprest, setSelectedImprest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // -----------------------------------------------------------------
  // 1. Fetch Imprest Applications (disbursed only)
  // -----------------------------------------------------------------
  useEffect(() => {
    const fetchImprestApplications = async () => {
      try {
        const result = await graphqlQuery(approvedImprestApplicationsNotSurrendered, base_url);
        const disbursed = (result || []).filter((app) => app.isDisbursed);
        setImprestApplications(disbursed);
      } catch (error) {
        console.error("Error fetching imprest applications:", error);
        setImprestApplications([]);
      } finally {
        setLoading(false);
      }
    };
    fetchImprestApplications();
  }, []);

  // -----------------------------------------------------------------
  // 2. Dropdown Options
  // -----------------------------------------------------------------
  const imprestOptions = useMemo(
    () =>
      imprestApplications.map((app) => ({
        value: app.id,
        label: `${app.description} (#${app.id}) – ${app.currencyCode} ${app.amount.toFixed(2)}`,
      })),
    [imprestApplications]
  );

  // -----------------------------------------------------------------
  // 3. Formik schema & initial values
  // -----------------------------------------------------------------
  const validationSchema = yup.object({
    applicationId: yup.number().required("Please select an imprest application"),
    description: yup.string(),
  });

  const initialValues = {
    applicationId: data?.applicationId ?? "",
    description: data?.description ?? "",
  };

  // -----------------------------------------------------------------
  // 4. Stepper definition (visual only – one step)
  // -----------------------------------------------------------------
  const steps = [
    {
      label: "Select Imprest",
      description: "Choose the imprest you want to surrender",
      icon: "receipt_long",
    },
  ];

  const progress = ((activeStep + 1) / steps.length) * 100;

  // -----------------------------------------------------------------
  // 5. Submit
  // -----------------------------------------------------------------
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        applicationId: parseInt(values.applicationId, 10)
      };

      const mutationName = isEditing
        ? "updateImprestSurrender"
        : "createImprestSurrenderFromImprest";

      const response = await generateAndExecuteMutation(
        mutationName,
        payload,
        "approveModel",
        base_url
      );

      if (response?.[mutationName]) {
        onClose?.();
        onAction?.();
        Swal.fire({ icon: "success", title: "Success!", text: "Imprest surrender created successfully." });
      }
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire({ icon: "error", title: "Error!", text: error.message || "Failed to create surrender." });
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------
  // 6. Loading UI
  // -----------------------------------------------------------------
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // -----------------------------------------------------------------
  // 7. Render
  // -----------------------------------------------------------------
  return (
    <Box m="20px">
      {/* Header */}
      <Typography variant="h4" fontWeight={600} color={colors.grey[100]} gutterBottom>
        {isEditing ? "Update Surrender" : "Create Imprest Surrender"}
      </Typography>
      <Typography variant="body1" color={colors.grey[300]} mb={3}>
        Select the imprest application you wish to surrender.
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, isSubmitting, validateForm }) => {
          // Update preview when selection changes
          const selectedApp = imprestApplications.find((a) => a.id === values.applicationId);
          if (selectedApp && selectedApp !== selectedImprest) {
            setSelectedImprest(selectedApp);
          }

          return (
            <Form>
              {/* Progress Bar */}
              {/* <Paper elevation={2} sx={{ mb: 3, overflow: "hidden", bgcolor: colors.primary[400] }}>
                <Box p={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" fontWeight={600} color={colors.grey[100]}>
                      Step {activeStep + 1} of {steps.length}: {steps[activeStep].label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color={colors.greenAccent[500]}>
                      {Math.round(progress)}% Complete
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: colors.primary[500],
                      "& .MuiLinearProgress-bar": { borderRadius: 1, bgcolor: colors.greenAccent[500] },
                    }}
                  />
                  <Typography variant="body2" color={colors.grey[300]} mt={1}>
                    {steps[activeStep].description}
                  </Typography>
                </Box>
              </Paper> */}

              {/* Stepper (visual) */}
              {/* <Paper elevation={1} sx={{ mb: 3, p: 2, bgcolor: colors.primary[400] }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((step, idx) => (
                    <Step key={step.label} completed={completedSteps.has(idx)}>
                      <StepButton onClick={() => setActiveStep(idx)}>
                        <Box textAlign="center">
                          <Typography variant="h6">{step.icon === "receipt_long" ? <ReceiptLong /> : "📝"}</Typography>
                          <StepLabel>
                            <Typography
                              variant="caption"
                              sx={{ color: activeStep === idx ? colors.grey[100] : colors.grey[300] }}
                            >
                              {step.label}
                            </Typography>
                          </StepLabel>
                        </Box>
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
              </Paper> */}

              {/* Form Fields */}
              <Paper elevation={2} sx={{ p: 3, mb: 3, minHeight: 100, bgcolor: colors.primary[400] }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Autocomplete
                      options={imprestOptions}
                      getOptionLabel={(opt) => opt.label}
                      value={imprestOptions.find((o) => o.value === values.applicationId) || null}
                      onChange={(_, newVal) => {
                        setFieldValue("applicationId", newVal?.value ?? "");
                        // ✅ Auto-populate description from selected imprest
                        const selectedApp = imprestApplications.find(
                          (a) => a.id === newVal?.value
                        );
                        setFieldValue("description", selectedApp?.description ?? "");
                        setCompletedSteps((s) => new Set([...s, activeStep]));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Imprest Application"
                          variant="filled"
                          error={touched.applicationId && !!errors.applicationId}
                          helperText={(touched.applicationId && errors.applicationId) || " "}
                          InputProps={{
                            ...params.InputProps,
                            style: { color: colors.grey[100] },
                          }}
                          InputLabelProps={{ style: { color: colors.grey[300] } }}
                          sx={{
                            "& .MuiFormHelperText-root": { color: colors.grey[300] },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Description Field - Auto-populated from Imprest */}
                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Description (from Imprest Application)"
                      variant="filled"
                      value={values.description}
                      onChange={(e) => setFieldValue("description", e.target.value)}
                      placeholder="Description will be auto-populated from the selected imprest application"
                      InputProps={{
                        style: { color: colors.grey[100] },
                        readOnly: !isEditing || !values.applicationId,
                      }}
                      InputLabelProps={{ style: { color: colors.grey[300] } }}
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: colors.primary[500],
                        },
                      }}
                      disabled={!values.applicationId}
                      helperText={values.applicationId ? "This description is from the imprest application" : "Select an imprest to populate description"}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Preview Card */}
              {selectedImprest && (
                <Card variant="outlined" sx={{ mt: 3, bgcolor: colors.primary[500] }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ bgcolor: colors.greenAccent[700] }} gutterBottom>
                      <ReceiptLong sx={{ verticalAlign: "middle", mr: 1 }} />
                      Selected Imprest Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Title:</strong>
                        </Typography>
                        <Typography variant="body1">{selectedImprest.title}</Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Amount:</strong>
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          <AttachMoney fontSize="small" />
                          {selectedImprest.amount.toFixed(2)} {selectedImprest.currencyCode}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Disbursed On:</strong>
                        </Typography>
                        <Typography variant="body1">
                          <CalendarToday fontSize="small" sx={{ mr: 0.5 }} />
                          {new Date(selectedImprest.disbursedDate).toLocaleDateString()}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Status:</strong>
                        </Typography>
                        <Chip label={selectedImprest.statusText || "Disbursed"} color="success" size="small" />
                      </Grid>

                      {selectedImprest.description && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            <Description fontSize="small" sx={{ mr: 0.5 }} />
                            <strong>Description:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic" }}>
                            {selectedImprest.description}
                          </Typography>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Reference: {selectedImprest.paymentReference} | Bank: {selectedImprest.disbursementBankAccountName}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Sticky Navigation */}
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  position: "sticky",
                  bottom: 0,
                  bgcolor: colors.primary[400],
                  zIndex: 10,
                }}
              >
                <Button
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep((p) => Math.max(p - 1, 0))}
                  startIcon={<BackIcon />}
                  size="large"
                  variant="outlined"
                  sx={{
                    borderColor: colors.grey[400],
                    color: colors.grey[100],
                    "&:hover": { borderColor: colors.greenAccent[500], bgcolor: colors.primary[500] },
                  }}
                >
                  Back
                </Button>

                <Typography variant="body2" color={colors.grey[300]}>
                  {Object.keys(errors).length > 0 && (
                    <span style={{ color: colors.redAccent[500] }}>Please fix errors before submitting</span>
                  )}
                </Typography>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || !values.applicationId}
                  size="large"
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                  sx={{
                    minWidth: 150,
                    fontWeight: 600,
                    bgcolor: colors.greenAccent[500],
                    color: colors.grey[900],
                    "&:hover": { bgcolor: colors.greenAccent[600] },
                  }}
                >
                  {isSubmitting ? "Saving…" : isEditing ? "Update" : "Create Surrender"}
                </Button>
              </Paper>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default ImprestSurrenderForm;